import { Fragment, useMemo, type ReactNode } from "react";
import { AppLink } from "../navigation/AppLink";
import { RenderFaultBoundary } from "../RenderFaultBoundary";
import { parseAssistantAnswer, type AnswerInline, type AnswerLine } from "./assistant-answer";
import { resolveAssistantLink, type AssistantLinkContext, type ResolvedAssistantLink } from "./assistant-links";

interface AssistantAnswerProps {
    /** The answer as it stands — all of it, or what has streamed in so far. */
    text: string;
    /** Decides which screens this user may be linked to. */
    linkContext?: AssistantLinkContext;
    /** Follows a link through the shell; without it a link moves the app itself. */
    onFollowLink?: (link: ResolvedAssistantLink) => void;
}

const LINK_CLASS = "text-primary underline underline-offset-2 transition-colors duration-150 hover:text-foreground";

/**
 * One answer of the assistant, drawn from the little Markdown it writes
 * (`assistant-answer.ts`) as React elements and never as HTML: the text is
 * the model's own. A link is a link only to a screen `assistant-links.ts`
 * allows and the shell would open for this user; any other reads as its
 * label. Every paragraph, list and item states its size, since a bare `<p>`
 * is drawn at the base size inside a window set in `text-sm`.
 *
 * Each answer is drawn inside a boundary of its own, so one that cannot be
 * drawn costs its formatting and nothing else: its words stand in, plain.
 */
export function AssistantAnswer(props: AssistantAnswerProps) {
    return (
        <RenderFaultBoundary
            part="askOptivo.chat.turn"
            retryKey={props.text}
            fallback={<p className="whitespace-pre-line break-words text-sm leading-relaxed">{props.text}</p>}
        >
            <AnswerBlocks {...props} />
        </RenderFaultBoundary>
    );
}

function AnswerBlocks({ text, linkContext, onFollowLink }: AssistantAnswerProps) {
    // The window draws again with every key pressed in the composer: an
    // answer is read again only once its text has changed.
    const blocks = useMemo(() => parseAssistantAnswer(text), [text]);

    const inline = (nodes: AnswerInline[]): ReactNode[] => nodes.map((node, index) => {
        switch (node.kind) {
            case "text":
                return <Fragment key={index}>{node.text}</Fragment>;
            case "strong":
                return <strong key={index} className="font-semibold">{inline(node.children)}</strong>;
            case "code":
                return <code key={index} className="rounded-sm bg-muted px-1 font-mono text-xs">{node.text}</code>;
            case "link": {
                const link = resolveAssistantLink(node.target, linkContext);
                if (!link) return <Fragment key={index}>{node.label}</Fragment>;
                return (
                    <AppLink
                        key={index}
                        to={link.href}
                        onNavigate={onFollowLink ? () => onFollowLink(link) : undefined}
                        className={LINK_CLASS}
                    >
                        {node.label}
                    </AppLink>
                );
            }
        }
    });

    const lines = (rows: AnswerLine[]): ReactNode[] => rows.flatMap((row, index) => [
        ...(index > 0 ? [<br key={`br-${index}`} />] : []),
        <Fragment key={index}>{inline(row)}</Fragment>,
    ]);

    return (
        <div className="space-y-2 break-words text-sm leading-relaxed">
            {blocks.map((block, index) => {
                if (block.kind === "paragraph") {
                    return <p key={index} className="text-sm leading-relaxed">{lines(block.lines)}</p>;
                }
                const items = block.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="text-sm leading-relaxed">{lines(item)}</li>
                ));
                return block.ordered ? (
                    <ol key={index} start={block.start} className="list-decimal space-y-1 pl-5 text-sm leading-relaxed">{items}</ol>
                ) : (
                    <ul key={index} className="list-disc space-y-1 pl-5 text-sm leading-relaxed">{items}</ul>
                );
            })}
        </div>
    );
}
